import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';

import { StyledEmbla } from '@/components/Carousel/styled';
import Carousel from '@/components/Carousel';

import { useHoldingsLimits } from '@/hooks/portfolio/useHoldingsLimits';

import { formatterPercentage } from '@/utilities/format';
import { pluralise } from '@/utilities/pluralise';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';

import PositiveNegativeValue from '@/components/PositiveNegativeValue';
import Tooltip from '@/components/Tooltip';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

interface Props {
  portfolio: Portfolio;
}

export const TransactionsMetrics = ({ portfolio }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { holdings } = useHoldingsLimits({
    portfolioHoldings: portfolio.holdings,
  });

  const holdingOrHoldings = pluralise(holdings.length, 'holding', 'holdings');

  const {
    value,
    estimatedFutureDividendYield,
    realisedReturn,
    dividendPaid,
    currencyReturn,
    totalReturn,
    realisedReturnPercent,
    dividendPaidPercent,
    currencyReturnPercent,
    totalReturnPercent,
    estimatedYearlyDividendFuture1y,
    unrealisedReturn,
    unrealisedReturnPercent,
    annualisedReturn,
  } = portfolio.analysis;

  return (
    <StyledEmbla>
      <Carousel
        align="center"
        containScroll="trimSnaps"
        showArrows={!isMobileScreen}
        slidesToScroll={1}
      >
        <div role="list" className="flex flex-row items-end gap-x4 text-solid">
          <div className="grid grid-cols-[4px_max-content] gap-x1_25">
            <div className="w-x0_25 rounded-full bg-legacy-darkergray" />
            <div>
              <p className="m-0 text-xl font-medium" data-testid="total-value">
                {portfolio.isAnalysisRestricted
                  ? 'n/a'
                  : formatCurrencyToScaleTruncated(
                      value,
                      portfolio.currencyISO
                    )}
              </p>
              <div className="m-0 text-tiny text-soft">
                <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                  <Tooltip
                    title={
                      <div className="max-w-[260px]">
                        <p className="m-0 text-left text-tiny text-legacy-spacegray">
                          Current market value of your portfolio.
                        </p>
                      </div>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                      Total Value
                    </span>
                  </Tooltip>
                  •{' '}
                  {portfolio.isAnalysisRestricted
                    ? '0 holdings'
                    : `${holdings.length} ${holdingOrHoldings}`}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-end gap-x3">
            <div className="grid grid-cols-[4px_max-content] gap-x1_25">
              <div className="w-x0_25 rounded-full bg-legacy-darkergray" />
              <div>
                <p
                  className="m-0 text-xl font-medium"
                  data-testid="total-return"
                >
                  {portfolio.isAnalysisRestricted
                    ? 'n/a'
                    : formatCurrencyToScaleTruncated(
                        totalReturn,
                        portfolio.currencyISO
                      )}
                </p>
                <div className="m-0 text-tiny text-soft">
                  <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                    <Tooltip
                      title={
                        <div className="max-w-[260px]">
                          <p className="m-0 text-left text-tiny text-legacy-spacegray">
                            Sum of unrealised, realised, dividends, and currency
                            returns.
                          </p>
                        </div>
                      }
                      light
                      placement="top"
                      zIndex={99999}
                    >
                      <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                        Total Returns
                      </span>
                    </Tooltip>
                    {portfolio.isAnalysisRestricted ? null : (
                      <PositiveNegativeValue value={totalReturnPercent}>
                        {formatterPercentage(totalReturnPercent)}
                      </PositiveNegativeValue>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[4px_max-content] gap-x1_25">
              <div className="w-x0_25 rounded-full bg-legacy-darkergray" />
              <div>
                <p
                  className="m-0 text-xl font-medium"
                  data-testid="annualised-return"
                >
                  {portfolio.isAnalysisRestricted
                    ? 'n/a'
                    : formatterPercentage(annualisedReturn)}
                </p>

                <div className="m-0 grid cursor-help text-tiny text-soft">
                  <Tooltip
                    title={
                      <div className="max-w-[260px]">
                        <p className="m-0 text-left text-tiny text-legacy-spacegray">
                          The annualised return rate of your portfolio,
                          calculated using the Internal Rate of Return (IRR).
                        </p>
                      </div>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                      Annualised (IRR)
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[max-content]">
              <p className="m-0 text-xs font-medium" data-testid="unrealised">
                {portfolio.isAnalysisRestricted
                  ? 'n/a'
                  : formatCurrencyToScaleTruncated(
                      unrealisedReturn,
                      portfolio.currencyISO
                    )}
              </p>
              <div className="m-0 text-tiny text-soft">
                <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                  <Tooltip
                    title={
                      <div className="max-w-[260px]">
                        <p className="m-0 text-left text-tiny text-legacy-spacegray">
                          Gains or losses on holdings you haven't sold.
                        </p>
                      </div>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                      Unrealised
                    </span>
                  </Tooltip>
                  {portfolio.isAnalysisRestricted ? null : (
                    <PositiveNegativeValue value={unrealisedReturnPercent}>
                      {formatterPercentage(unrealisedReturnPercent)}
                    </PositiveNegativeValue>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[max-content]">
              <p className="m-0 text-xs font-medium" data-testid="realised">
                {portfolio.isAnalysisRestricted
                  ? 'n/a'
                  : formatCurrencyToScaleTruncated(
                      realisedReturn,
                      portfolio.currencyISO
                    )}
              </p>
              <div className="m-0 text-tiny text-soft">
                <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                  <Tooltip
                    title={
                      <div className="max-w-[260px]">
                        <p className="m-0 text-left text-tiny text-legacy-spacegray">
                          Gains or losses from sold holdings.
                        </p>
                      </div>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                      Realised
                    </span>
                  </Tooltip>
                  {portfolio.isAnalysisRestricted ? null : (
                    <PositiveNegativeValue value={realisedReturnPercent}>
                      {formatterPercentage(realisedReturnPercent)}
                    </PositiveNegativeValue>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[max-content]">
              <p className="m-0 text-xs font-medium" data-testid="dividends">
                {portfolio.isAnalysisRestricted
                  ? 'n/a'
                  : formatCurrencyToScaleTruncated(
                      dividendPaid,
                      portfolio.currencyISO
                    )}
              </p>
              <div className="m-0 text-tiny text-soft">
                <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                  <Tooltip
                    title={
                      <div className="max-w-[260px]">
                        <p className="m-0 text-left text-tiny text-legacy-spacegray">
                          Income earned from dividend payouts.
                        </p>
                      </div>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                      Dividends
                    </span>
                  </Tooltip>
                  {portfolio.isAnalysisRestricted ? null : (
                    <PositiveNegativeValue value={dividendPaidPercent}>
                      {formatterPercentage(dividendPaidPercent)}
                    </PositiveNegativeValue>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-[max-content]">
              <p className="m-0 text-xs font-medium" data-testid="currency">
                {portfolio.isAnalysisRestricted
                  ? 'n/a'
                  : formatCurrencyToScaleTruncated(
                      currencyReturn,
                      portfolio.currencyISO
                    )}
              </p>
              <div className="m-0 text-tiny text-soft">
                <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                  <Tooltip
                    title={
                      <div className="max-w-[260px]">
                        <p className="m-0 text-left text-tiny text-legacy-spacegray">
                          Impact of currency rate changes on realised gains from
                          foreign stocks.
                        </p>
                      </div>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                      Currency
                    </span>
                  </Tooltip>
                  {portfolio.isAnalysisRestricted ? null : (
                    <PositiveNegativeValue value={currencyReturnPercent}>
                      {formatterPercentage(currencyReturnPercent)}
                    </PositiveNegativeValue>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-[4px_max-content] gap-x1_25">
            <div className="w-x0_25 rounded-full bg-legacy-darkergray" />
            <div>
              <div className="grid grid-cols-[max-content_max-content] items-baseline gap-x1">
                <p
                  className="m-0 text-xl font-medium"
                  data-testid="est-dividend"
                >
                  {portfolio.isAnalysisRestricted
                    ? 'n/a'
                    : formatCurrencyToScaleTruncated(
                        estimatedYearlyDividendFuture1y,
                        portfolio.currencyISO
                      )}
                </p>
                <p className="m-0 text-base font-medium text-p-white-170">
                  /yr
                </p>
              </div>
              <div className="m-0 text-tiny text-soft">
                <div className="grid cursor-help grid-cols-[max-content_auto] gap-x0_5">
                  <Tooltip
                    title={
                      <div className="max-w-[260px]">
                        <p className="m-0 text-left text-tiny text-legacy-spacegray">
                          Expected future income from dividends in the next 12m.
                        </p>
                      </div>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <span className="m-0 border-0 border-b-2 border-dotted border-legacy-darkergray text-tiny text-soft">
                      Est. Dividends
                    </span>
                  </Tooltip>
                  {portfolio.isAnalysisRestricted
                    ? null
                    : `• ${formatterPercentage(
                        estimatedFutureDividendYield
                      )} Yield`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Carousel>
    </StyledEmbla>
  );
};
